import { createContext, useContext } from 'react';
import { APP_LIST, type AppCode } from '../constants';

export const GLOBAL_SETTINGS_STORAGE_KEY = 'uama:global-settings';

export type ServiceNetwork = 'facebook' | 'tiktok' | 'twitter';

export interface GlobalSettings {
  app?: AppCode;
  network?: ServiceNetwork;
}

export interface GlobalSettingsContextValue {
  selectedApp?: AppCode;
  setSelectedApp: (app: AppCode) => void;
  selectedNetwork?: ServiceNetwork;
  setSelectedNetwork: (network: ServiceNetwork) => void;
}

const APP_CODES: readonly string[] = APP_LIST.map(({ code }) => code);

const isAppCode = (value: string | null | undefined): value is AppCode =>
  !!value && APP_CODES.includes(value);

export const readAppFromSearch = (search: string): AppCode | undefined => {
  const params = new URLSearchParams(search.startsWith('?') ? search.slice(1) : search);
  const app = params.get('app');

  return isAppCode(app) ? app : undefined;
};

export const canAccessApp = (
  app: AppCode | undefined,
  access: { isSuperuser: boolean; apps?: readonly string[] },
): app is AppCode => {
  if (!app) return false;
  if (access.isSuperuser) return true;

  return Boolean(access.apps?.includes(app));
};

export const readGlobalSettings = (): GlobalSettings => {
  try {
    const raw = window.localStorage.getItem(GLOBAL_SETTINGS_STORAGE_KEY);
    if (!raw) return {};

    return JSON.parse(raw) as GlobalSettings;
  } catch {
    // Storage may be unavailable or corrupted - fall back to empty settings.
    return {};
  }
};

export const writeGlobalSettings = (settings: GlobalSettings): void => {
  try {
    window.localStorage.setItem(GLOBAL_SETTINGS_STORAGE_KEY, JSON.stringify(settings));
  } catch {
    // Ignore write errors (e.g. storage disabled in private mode).
  }
};

/**
 * Persists an accessible `?app=` from the URL into `uama:global-settings`.
 * Returns that app when the user may use it (`/me` apps or superuser); otherwise
 * leaves storage unchanged and returns undefined.
 */
export const applyAccessibleAppFromSearch = (
  search: string,
  access: { isSuperuser: boolean; apps?: readonly string[] },
): AppCode | undefined => {
  const app = readAppFromSearch(search);
  if (!canAccessApp(app, access)) return undefined;

  const current = readGlobalSettings();
  if (current.app !== app) {
    writeGlobalSettings({ ...current, app });
  }

  return app;
};

const DEFAULT_CONTEXT: GlobalSettingsContextValue = {
  selectedApp: undefined,
  setSelectedApp: () => undefined,
  selectedNetwork: undefined,
  setSelectedNetwork: () => undefined,
};

export const GlobalSettingsContext =
  createContext<GlobalSettingsContextValue>(DEFAULT_CONTEXT);

export const useGlobalSettings = (): GlobalSettingsContextValue =>
  useContext(GlobalSettingsContext);
